import type { ItemTreeSetEntry } from '@/common/api/types/items';

export type TreeSetDiff = {
  addedIds: number[];
  removedIds: number[];
  movedIds: number[];
  structuralChangedIds: number[];
  fieldChangedIds: number[];
};

export const EMPTY_TREE_SET_DIFF: TreeSetDiff = {
  addedIds: [],
  removedIds: [],
  movedIds: [],
  structuralChangedIds: [],
  fieldChangedIds: [],
};

export const isEmptyTreeSetDiff = (diff: TreeSetDiff): boolean =>
  diff.addedIds.length === 0 &&
  diff.removedIds.length === 0 &&
  diff.movedIds.length === 0 &&
  diff.structuralChangedIds.length === 0 &&
  diff.fieldChangedIds.length === 0;

/**
 * Every id the diff touches, including gone ones. Deduplicated — `movedIds`/`structuralChangedIds`
 * share rows, so one can land in both.
 */
export const allChangedIds = (diff: TreeSetDiff): number[] => [
  ...new Set([
    ...diff.addedIds,
    ...diff.removedIds,
    ...diff.movedIds,
    ...diff.structuralChangedIds,
    ...diff.fieldChangedIds,
  ]),
];

/** `allChangedIds` minus `removedIds` — for callers with no row left to act on for a removal. */
export const visibleChangedIds = (diff: TreeSetDiff): number[] => [
  ...new Set([
    ...diff.addedIds,
    ...diff.movedIds,
    ...diff.structuralChangedIds,
    ...diff.fieldChangedIds,
  ]),
];

export const positionalChangedIds = (diff: TreeSetDiff): number[] => [
  ...new Set([
    ...diff.addedIds,
    ...diff.removedIds,
    ...diff.movedIds,
    ...diff.structuralChangedIds,
  ]),
];

/**
 * Indices of the longest increasing subsequence of `values` — the smallest set of rows whose
 * removal leaves the rest in original order: the rows that actually moved.
 */
const longestIncreasingSubsequence = (values: number[]): Set<number> => {
  if (values.length === 0) return new Set();

  // tailIndices[k] = index of the smallest tail of a length-(k+1) increasing subsequence;
  // previous[] threads the chain to recover it.
  const tailIndices: number[] = [];
  const previous = new Array<number>(values.length).fill(-1);

  for (let i = 0; i < values.length; i += 1) {
    let low = 0;
    let high = tailIndices.length;
    while (low < high) {
      const mid = (low + high) >> 1;
      if ((values[tailIndices[mid] as number] as number) < (values[i] as number)) low = mid + 1;
      else high = mid;
    }
    if (low > 0) previous[i] = tailIndices[low - 1] as number;
    tailIndices[low] = i;
  }

  const result = new Set<number>();
  let cursor = tailIndices[tailIndices.length - 1] ?? -1;
  while (cursor !== -1) {
    result.add(cursor);
    cursor = previous[cursor] as number;
  }
  return result;
};

const hasPositionalChange = (previous: ItemTreeSetEntry, entry: ItemTreeSetEntry): boolean =>
  previous.matched !== entry.matched || previous.parentId !== entry.parentId;

const hasFieldOnlyChange = (previous: ItemTreeSetEntry, entry: ItemTreeSetEntry): boolean =>
  previous.itemTypeId !== entry.itemTypeId ||
  previous.hasExternalKey !== entry.hasExternalKey ||
  previous.parentManagedByIntegration !== entry.parentManagedByIntegration ||
  previous.isDeletedInItemStore !== entry.isDeletedInItemStore ||
  previous.loopId !== entry.loopId;

/**
 * Compares two tree-set responses as *structure*. Moves are the minimal set of ids that broke
 * relative order — index-by-index comparison would report forty moves for one row shifting from
 * index 3 to 40, indistinguishable from a full reshuffle.
 *
 * Reported as `structuralChangedIds` even at the same index: a `matched` transition (unmatched
 * tree-completion ancestors stay in the response instead of disappearing), or a `parentId` change
 * (reparented, even if this flat list's position didn't move).
 */
export const diffTreeSet = (
  before: readonly ItemTreeSetEntry[],
  after: readonly ItemTreeSetEntry[],
): TreeSetDiff => {
  const beforeIndexById = new Map<number, number>();
  const beforeById = new Map<number, ItemTreeSetEntry>();
  before.forEach((entry, index) => {
    beforeIndexById.set(entry.id, index);
    beforeById.set(entry.id, entry);
  });

  const afterIds = new Set<number>();
  const addedIds: number[] = [];
  const structuralChangedIds: number[] = [];
  const fieldChangedIds: number[] = [];
  const commonBeforeIndices: number[] = [];
  const commonIds: number[] = [];

  for (const entry of after) {
    afterIds.add(entry.id);
    const previousEntry = beforeById.get(entry.id);
    if (previousEntry === undefined) {
      addedIds.push(entry.id);
      continue;
    }
    if (hasPositionalChange(previousEntry, entry)) {
      structuralChangedIds.push(entry.id);
    } else if (hasFieldOnlyChange(previousEntry, entry)) {
      fieldChangedIds.push(entry.id);
    }
    commonBeforeIndices.push(beforeIndexById.get(entry.id) as number);
    commonIds.push(entry.id);
  }

  const removedIds = before.filter((entry) => !afterIds.has(entry.id)).map((entry) => entry.id);

  const stable = longestIncreasingSubsequence(commonBeforeIndices);
  const movedIds = commonIds.filter((_id, position) => !stable.has(position));

  return { addedIds, removedIds, movedIds, structuralChangedIds, fieldChangedIds };
};

/**
 * Widens `roots` to every descendant via `parentId`, so a reparent's subtree counts as
 * attributable to the row actually moved.
 */
export const withDescendants = (
  roots: ReadonlySet<number> | readonly number[],
  entries: readonly ItemTreeSetEntry[],
): Set<number> => {
  const scope = new Set(roots);
  if (scope.size === 0) return scope;

  const childrenByParent = new Map<number, number[]>();
  for (const entry of entries) {
    if (entry.parentId === null) continue;
    const siblings = childrenByParent.get(entry.parentId);
    if (siblings) siblings.push(entry.id);
    else childrenByParent.set(entry.parentId, [entry.id]);
  }

  const queue = [...scope];
  while (queue.length > 0) {
    const next = queue.pop() as number;
    for (const child of childrenByParent.get(next) ?? []) {
      if (scope.has(child)) continue;
      scope.add(child);
      queue.push(child);
    }
  }
  return scope;
};

/**
 * Widens `roots` to every ancestor via `parentId`, so a delete that flips an ancestor's own
 * `matched` (e.g. removing its last matching child) counts as attributable too.
 */
export const withAncestors = (
  roots: ReadonlySet<number> | readonly number[],
  entries: readonly ItemTreeSetEntry[],
): Set<number> => {
  const scope = new Set(roots);
  if (scope.size === 0) return scope;

  const parentById = new Map(entries.map((entry) => [entry.id, entry.parentId]));

  const queue = [...scope];
  while (queue.length > 0) {
    const id = queue.pop() as number;
    const parentId = parentById.get(id);
    if (parentId === null || parentId === undefined || scope.has(parentId)) continue;
    scope.add(parentId);
    queue.push(parentId);
  }
  return scope;
};

/**
 * Widens `scope` to a newly appearing "tree-completion" ancestor of a row already in scope — the
 * bridging row a reparent introduces when moving something out from under its shown parent.
 * `withDescendants` can't reach this; it only walks `parentId` links already in *before*. Walks
 * as many new links deep as the reparent introduced.
 */
export const withNewAncestors = (
  scope: ReadonlySet<number>,
  addedIds: readonly number[],
  after: readonly ItemTreeSetEntry[],
): Set<number> => {
  const widened = new Set(scope);
  if (addedIds.length === 0) return widened;

  const addedIdSet = new Set(addedIds);
  const parentById = new Map(after.map((entry) => [entry.id, entry.parentId]));

  const queue = [...widened];
  while (queue.length > 0) {
    const id = queue.pop() as number;
    const parentId = parentById.get(id);
    if (parentId === null || parentId === undefined) continue;
    if (!addedIdSet.has(parentId) || widened.has(parentId)) continue;
    widened.add(parentId);
    queue.push(parentId);
  }
  return widened;
};
